/**
 * Story 24.6 — the app-nav rail's width, as ONE pure function of the window
 * width and the pin state, plus the pin store.
 *
 * The `<Sidebar>` (`src/components/layout/Sidebar.tsx`) has three bands, keyed
 * to Tailwind's `lg` (1024) and `xl` (1280) breakpoints:
 *
 *   - `off-canvas` (< 1024): the Sidebar is not rendered at all (Story 24.7 —
 *     the BottomTabBar carries navigation), so it takes no width → 0. The
 *     band keeps its old name.
 *   - `rail` (1024–1279): a 64px icon rail in flow; the pin toggle at its foot
 *     expands it to 240px, still in flow (it pushes the content) → 64 / 240.
 *   - `full` (≥ 1280): 240px with labels whatever the pin → 240.
 *
 * 🔴 NEVER RE-TYPE 240 OR 64. The Sidebar's `lg:w-16` / `lg:w-60` tokens,
 * `useViewportRange`'s render-1 estimate, the toast's left offset, the jsdom
 * suites and `tests/e2e/navigation.spec.ts` all read this module.
 *
 * Dependency-free (no React, no `window` at import time) so the e2e suite can
 * import it under Node.
 */

export const APP_NAV_RAIL_BREAKPOINTS = { rail: 1024, full: 1280 } as const;

/** The same signal as Tailwind's `lg` / `xl` variants. */
export const APP_NAV_RAIL_QUERIES = {
  rail: '(min-width: 1024px)',
  full: '(min-width: 1280px)',
} as const;

export type AppNavRailBand = 'off-canvas' | 'rail' | 'full';

/** The 64px icon rail (DESIGN.md `spacing.sidebar-rail`). */
const RAIL_WIDTH = 64;
/** The full sidebar (DESIGN.md `spacing.sidebar`). */
const FULL_WIDTH = 240;

export function railBandForWidth(windowWidth: number): AppNavRailBand {
  if (windowWidth >= APP_NAV_RAIL_BREAKPOINTS.full) return 'full';
  if (windowWidth >= APP_NAV_RAIL_BREAKPOINTS.rail) return 'rail';
  return 'off-canvas';
}

export function railWidthForBand(band: AppNavRailBand, pinned: boolean): 0 | 64 | 240 {
  if (band === 'off-canvas') return 0;
  if (band === 'full') return FULL_WIDTH;
  return pinned ? FULL_WIDTH : RAIL_WIDTH;
}

/** The in-flow width the app-nav rail takes from the page: 0 / 64 / 240. */
export function appNavRailWidth(windowWidth: number, pinned: boolean): 0 | 64 | 240 {
  return railWidthForBand(railBandForWidth(windowWidth), pinned);
}

/**
 * How far a rail tooltip sits off its trigger. The rows are inset 8px from
 * the rail's edge (the nav's `px-2`) and the pin toggle 10px (centred 44px in
 * 64), so the shared Tooltip's default 4px would overlap the rail. 12px lands
 * every tooltip clear of the rail's right edge (hairline included).
 */
export const APP_NAV_RAIL_TOOLTIP_OFFSET = 12;

/* ── The pin store ─────────────────────────────────────────────────────────
 * Remembered per browser under `amatis.sidebar.pinned` ('true' / 'false').
 * Local storage is optional (epic rule): every access is wrapped, and when
 * storage throws the in-memory value carries the session (starts unpinned).
 */

export const SIDEBAR_PINNED_STORAGE_KEY = 'amatis.sidebar.pinned';

let memoryPinned = false;
/** Set once a storage read or write has thrown: from then on memory is the truth. */
let storageFailed = false;
const subscribers = new Set<() => void>();

export function readSidebarPinned(): boolean {
  if (storageFailed) return memoryPinned;
  try {
    // Mirror every successful read, so storage that starts throwing later
    // keeps the last known pin instead of snapping back to the rail.
    memoryPinned = globalThis.localStorage?.getItem(SIDEBAR_PINNED_STORAGE_KEY) === 'true';
    return memoryPinned;
  } catch {
    storageFailed = true;
    return memoryPinned;
  }
}

export function setSidebarPinned(value: boolean): void {
  memoryPinned = value;
  try {
    globalThis.localStorage?.setItem(SIDEBAR_PINNED_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // Blocked storage: the in-memory value carries the rest of the session.
    storageFailed = true;
  }
  subscribers.forEach((fn) => fn());
}

export function subscribeSidebarPinned(fn: () => void): () => void {
  subscribers.add(fn);
  // Another tab's write changes what `readSidebarPinned` returns; tell React,
  // so every subscriber (Sidebar, toast) picks it up on the same render.
  const onStorage = (event: StorageEvent) => {
    if (event.key === SIDEBAR_PINNED_STORAGE_KEY) fn();
  };
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    subscribers.delete(fn);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

/** Test seam: forget the in-memory fallback (the jsdom setup clears storage, not this). */
export function resetSidebarPinnedForTests(): void {
  memoryPinned = false;
  storageFailed = false;
}
